import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Popover from '@radix-ui/react-popover';
import { cn } from '@/utils/cn';
import { SelectChevron } from '@/components/ui/Select';
import { filterTriggerFocus } from '@/components/ui/filterTriggerStyles';

/**
 * ColorPicker Component
 *
 * Modern color selection with predefined palette and custom color input
 * Uses Radix UI Popover for better positioning and accessibility
 */

interface ColorPickerProps {
  label?: string;
  value: string;
  onChange: (color: string) => void;
  error?: string;
  disabled?: boolean;
  compact?: boolean;
}

// Modern color palette with semantic groupings
const COLOR_PALETTE = [
  // Blues
  ['#3B82F6', '#60A5FA', '#2563EB', '#1D4ED8'],
  // Greens
  ['#10B981', '#34D399', '#059669', '#047857'],
  // Warm colors
  ['#F59E0B', '#FBBF24', '#F97316', '#EA580C'],
  // Reds & Pinks
  ['#EF4444', '#F87171', '#EC4899', '#DB2777'],
  // Purples
  ['#8B5CF6', '#A78BFA', '#6366F1', '#4F46E5'],
  // Teals & Cyans
  ['#06B6D4', '#22D3EE', '#14B8A6', '#0D9488'],
];

// Flatten for easy iteration
const ALL_COLORS = COLOR_PALETTE.flat();

export const ColorPicker = ({
  label,
  value,
  onChange,
  error,
  disabled,
  compact = false
}: ColorPickerProps) => {
  useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [customColor, setCustomColor] = useState(value);
  const colorInputRef = useRef<HTMLInputElement>(null);

  // Sync custom color with value
  useEffect(() => {
    setCustomColor(value);
  }, [value]);

  const handleColorSelect = (color: string) => {
    onChange(color);
    setIsOpen(false);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setCustomColor(newColor);
    onChange(newColor);
  };

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    // Allow # followed by 0-6 hex digits, or empty for editing
    if (input === '' || /^#([0-9A-Fa-f]{0,6})$/.test(input)) {
      setCustomColor(input);
      if (/^#[0-9A-Fa-f]{6}$/.test(input)) {
        onChange(input);
      }
    }
  };

  const handleHexInputBlur = () => {
    // Ensure valid format on blur - revert if invalid
    if (customColor && !/^#[0-9A-Fa-f]{6}$/.test(customColor)) {
      setCustomColor(value);
    }
  };

  return (
    <div className={cn('w-full', compact && 'w-auto')}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'w-full h-[34px] lg:h-[42px] rounded-lg border border-gray-200',
              'flex items-center gap-3',
              filterTriggerFocus,
              'transition-[border-color,background-color,color,opacity] duration-200',
              'hover:border-gray-300',
              isOpen && 'border-primary-400',
              disabled && 'opacity-50 cursor-not-allowed',
              error && 'border-red-500',
              compact ? 'p-1.5 h-auto w-auto' : 'px-3'
            )}
          >
            <div
              className={cn(
                'rounded border border-gray-300 flex-shrink-0',
                compact ? 'w-6 h-6' : 'w-6 h-6'
              )}
              style={{ backgroundColor: value }}
            />
            {!compact && (
              <>
                <span className="text-sm text-gray-700 font-mono flex-1 text-left">{value}</span>
                <SelectChevron className="shrink-0 text-gray-500" />
              </>
            )}
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            className={cn(
              'bg-white rounded-xl shadow-xl border border-gray-200',
              'p-3 z-[200] w-[222px]',
              'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
            )}
            sideOffset={8}
            align="start"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            {/* Color Grid */}
            <div className="grid grid-cols-6 gap-1.5">
              {ALL_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => handleColorSelect(color)}
                  className={cn(
                    'w-7 h-7 rounded-md transition-all duration-150',
                    'hover:scale-110 hover:shadow-lg',
                    'border-2 border-transparent focus:outline-none focus-visible:border-gray-900',
                    value?.toUpperCase() === color.toUpperCase() && 'ring-2 ring-offset-1 ring-gray-900 scale-110'
                  )}
                  style={{ backgroundColor: color }}
                  aria-label={`Select color ${color}`}
                />
              ))}
            </div>

            {/* Divider */}
            <div className="my-3 border-t border-gray-100" />

            {/* Custom Color Section */}
            <div className="flex items-center gap-2">
              {/* Native Color Picker */}
              <div className="relative flex-shrink-0">
                <input
                  ref={colorInputRef}
                  type="color"
                  value={customColor}
                  onChange={handleCustomColorChange}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
                <div
                  className="w-8 h-8 rounded-md border-2 border-gray-200 shadow-inner cursor-pointer hover:border-gray-300 transition-colors"
                  style={{ backgroundColor: customColor }}
                />
              </div>

              {/* Hex Input */}
              <input
                type="text"
                value={customColor}
                onChange={handleHexInputChange}
                onBlur={handleHexInputBlur}
                placeholder="#3B82F6"
                className="w-full min-w-0 h-8 px-2 text-sm font-mono border border-gray-200 rounded-md focus:outline-none focus:border-primary-600 placeholder:text-gray-400"
                maxLength={7}
              />
            </div>

            <Popover.Arrow className="fill-white" />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      {error && (
        <p className="mt-1 text-xs text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
